import { foldName } from "../lexicon/fold.js";
import { GRAPES, grapeCodeOf } from "../lexicon/grapes.js";

/**
 * A family is one of our own deduction-tree groups: grapes a taster reaches by the same first
 * markers. Ids are stable and never translated; names are ours per locale. Accuracy by family and
 * confusion pairs aggregate on these ids, so a call is judged on the family even when the grape
 * is missed ("Gamay for Pinot Noir" is a near miss, "Riesling for Syrah" is not).
 */
export interface Family {
  readonly id: string;
  readonly nl: string;
  readonly en: string;
  /** Grape codes of the lexicon (`grapes.ts`), each in exactly one family. */
  readonly grapes: readonly string[];
}

export const FAMILIES: readonly Family[] = [
  {
    id: "light-high-acid-reds",
    nl: "Lichte rode wijnen met hoge zuren",
    en: "Light reds with high acidity",
    grapes: [
      "pinot-noir",
      "gamay",
      "pinot-meunier",
      "schiava",
      "frappato",
      "grignolino",
      "freisa",
      "zweigelt",
      "sankt-laurent",
      "dornfelder",
      "cinsault",
      "cesanese",
      "lambrusco",
    ],
  },
  {
    id: "cool-peppery-reds",
    nl: "Koele, peperige rode wijnen",
    en: "Cool-climate peppery reds",
    grapes: [
      "syrah",
      "cabernet-franc",
      "blaufrankisch",
      "mencia",
      "refosco",
      "teroldego",
      "lagrein",
      "carmenere",
    ],
  },
  {
    id: "structured-dark-reds",
    nl: "Gestructureerde, donkere rode wijnen",
    en: "Structured dark-fruited reds",
    grapes: [
      "cabernet-sauvignon",
      "merlot",
      "petit-verdot",
      "malbec",
      "tannat",
      "pinotage",
      "petite-sirah",
      "touriga-nacional",
      "touriga-franca",
      "tinta-barroca",
    ],
  },
  {
    id: "warm-ripe-reds",
    nl: "Warme, rijpe rode wijnen",
    en: "Warm-climate ripe reds",
    grapes: [
      "grenache",
      "zinfandel",
      "mourvedre",
      "carignan",
      "tempranillo",
      "bobal",
      "castelao",
      "trincadeira",
      "negroamaro",
      "nero-d-avola",
      "montepulciano",
    ],
  },
  {
    id: "firm-high-acid-reds",
    nl: "Strakke rode wijnen met tannine en zuur",
    en: "Firm reds with tannin and acid",
    grapes: [
      "nebbiolo",
      "sangiovese",
      "barbera",
      "aglianico",
      "dolcetto",
      "corvina",
      "sagrantino",
      "nerello-mascalese",
      "xinomavro",
      "agiorgitiko",
      "saperavi",
      "baga",
    ],
  },
  {
    id: "aromatic-whites",
    nl: "Aromatische witte wijnen",
    en: "Aromatic whites",
    grapes: [
      "gewurztraminer",
      "muscat",
      "scheurebe",
      "kerner",
      "bacchus",
      "torrontes",
      "viognier",
      "muscadelle",
      "malvasia",
      "petit-manseng",
    ],
  },
  {
    id: "high-acid-mineral-whites",
    nl: "Frisse, mineraalrijke witte wijnen",
    en: "Fresh, mineral high-acid whites",
    grapes: [
      "riesling",
      "chenin-blanc",
      "assyrtiko",
      "melon-de-bourgogne",
      "picpoul",
      "albarino",
      "furmint",
      "harslevelu",
      "aligote",
      "ribolla-gialla",
      "xarel-lo",
      "loureiro",
      "arinto",
      "gros-manseng",
      "cortese",
    ],
  },
  {
    id: "green-herbal-whites",
    nl: "Groene, kruidige witte wijnen",
    en: "Green, herbal whites",
    grapes: [
      "sauvignon-blanc",
      "verdejo",
      "gruner-veltliner",
      "colombard",
      "friulano",
      "vermentino",
      "arneis",
      "godello",
    ],
  },
  {
    id: "rich-rounded-whites",
    nl: "Volle, ronde witte wijnen",
    en: "Rich, rounded whites",
    grapes: [
      "chardonnay",
      "marsanne",
      "roussanne",
      "semillon",
      "grenache-blanc",
      "savagnin",
      "encruzado",
      "bical",
      "fiano",
      "greco",
      "pinot-blanc",
      "pinot-gris",
      "viura",
    ],
  },
  {
    id: "neutral-light-whites",
    nl: "Neutrale, lichte witte wijnen",
    en: "Neutral, light whites",
    grapes: [
      "trebbiano",
      "garganega",
      "glera",
      "silvaner",
      "muller-thurgau",
      "airen",
      "palomino",
      "pedro-ximenez",
      "verdicchio",
      "pecorino",
      "passerina",
      "falanghina",
      "parellada",
      "chasselas",
      "rkatsiteli",
      "grenache-gris",
    ],
  },
];

const FAMILY_OF: ReadonlyMap<string, Family> = new Map(
  FAMILIES.flatMap((family) => family.grapes.map((grape) => [grape, family] as const)),
);

export function familyById(id: string): Family | undefined {
  return FAMILIES.find((family) => family.id === id);
}

/** The family of a grape code, or undefined for a grape outside the taxonomy. */
export function familyOfGrape(code: string): Family | undefined {
  return FAMILY_OF.get(code);
}

/** The grape codes no family holds: empty while the taxonomy is complete (the lint's check). */
export function ungroupedGrapes(): string[] {
  return GRAPES.filter((grape) => !FAMILY_OF.has(grape.code)).map((grape) => grape.code);
}

/** How a stored call reads: a picked grape, text that names none, or no call at all. */
export type ReadCall =
  | { readonly kind: "grape"; readonly code: string }
  | { readonly kind: "legacy"; readonly text: string }
  | { readonly kind: "none" };

/**
 * Reads a call as stored: a canonical grape id, or legacy free text that folds (case, accents,
 * spacing, synonyms) to a grape we know. Text that names no grape stays legacy: shown as written,
 * judged incorrect, joining no family.
 */
export function readCall(call: string | undefined): ReadCall {
  const text = call?.trim() ?? "";
  if (foldName(text) === "") return { kind: "none" };
  const code = grapeCodeOf(text);
  return code === undefined ? { kind: "legacy", text } : { kind: "grape", code };
}
